import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, Input, Result,
  Row, Space, Statistic, Typography, message
} from 'antd';
import { DeleteOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const CONFIRMATION_TEXT = 'DELETE ALL JOURNAL ENTRIES';

const AccountingDangerZonePage = () => {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  const [summary, setSummary] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/accounting/danger-zone/preview');
      setSummary(response.data.data);
    } catch (error) {
      message.error(error.response?.data?.message || 'ไม่สามารถตรวจสอบข้อมูลสมุดรายวันได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) loadPreview();
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return <Result status="403" title="403" subTitle="หน้านี้อนุญาตเฉพาะ Super Admin" />;
  }

  const clearAll = async () => {
    setClearing(true);
    try {
      const response = await api.post('/api/accounting/danger-zone/clear-all', {
        confirmation,
        acknowledge_irreversible: acknowledged
      });
      message.success(`ล้างสมุดรายวัน ${response.data.data.deleted_journal_entries} รายการสำเร็จ`);
      setConfirmation('');
      setAcknowledged(false);
      await loadPreview();
    } catch (error) {
      message.error(error.response?.data?.message || 'ล้างข้อมูลสมุดรายวันไม่สำเร็จ');
    } finally {
      setClearing(false);
    }
  };

  const canClear = acknowledged && confirmation === CONFIRMATION_TEXT && Number(summary?.journal_entries || 0) > 0;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Space align="start" style={{ marginBottom: 20 }}>
        <WarningOutlined style={{ color: '#cf1322', fontSize: 32 }} />
        <div>
          <Title level={2} style={{ margin: 0, color: '#cf1322' }}>Accounting Danger Zone</Title>
          <Text type="secondary">ล้างข้อมูลสมุดรายวันทั้งหมดและคืนเอกสารให้พร้อมลงบัญชีใหม่</Text>
        </div>
      </Space>

      <Alert
        type="error"
        showIcon
        message="การดำเนินการนี้มีผลกับสมุดรายวันทั้งหมด"
        description="ระบบจะลบ Journal Entries, Journal Items และประวัติ Batch Posting ทั้งหมด พร้อม reset สถานะใบเสร็จและค่าใช้จ่าย เอกสารธุรกิจต้นทางจะไม่ถูกลบ แต่การดำเนินการนี้ย้อนกลับไม่ได้หากไม่มีฐานข้อมูลสำรอง"
        style={{ marginBottom: 18 }}
      />

      <Card loading={loading} bordered={false} style={{ borderRadius: 14, marginBottom: 18 }}>
        <Row gutter={[12, 12]}>
          <Col xs={12} md={8}><Statistic title="สมุดรายวัน" value={summary?.journal_entries || 0} /></Col>
          <Col xs={12} md={8}><Statistic title="รายการเดบิต/เครดิต" value={summary?.journal_items || 0} /></Col>
          <Col xs={12} md={8}><Statistic title="ประวัติ One Click" value={summary?.posting_runs || 0} /></Col>
          <Col xs={12} md={8}><Statistic title="ใบเสร็จที่ลงบัญชี" value={summary?.posted_receipts || 0} /></Col>
          <Col xs={12} md={8}><Statistic title="ค่าใช้จ่ายที่ลงบัญชี" value={summary?.posted_expenses || 0} /></Col>
        </Row>
        <Button icon={<ReloadOutlined />} onClick={loadPreview} loading={loading} style={{ marginTop: 16 }}>
          โหลดจำนวนล่าสุด
        </Button>
      </Card>

      <Card bordered={false} style={{ border: '1px solid #ffccc7', borderRadius: 14 }}>
        <Title level={4} style={{ color: '#cf1322' }}>ยืนยันการล้างข้อมูล</Title>
        <Paragraph>พิมพ์ข้อความต่อไปนี้ให้ตรงทุกตัว:</Paragraph>
        <Paragraph copyable code>{CONFIRMATION_TEXT}</Paragraph>
        <Input
          size="large"
          value={confirmation}
          onChange={event => setConfirmation(event.target.value)}
          placeholder={CONFIRMATION_TEXT}
          status={confirmation && confirmation !== CONFIRMATION_TEXT ? 'error' : ''}
        />
        <Checkbox checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} style={{ marginTop: 16 }}>
          ฉันเข้าใจว่าข้อมูลสมุดรายวันทั้งหมดจะถูกลบ และควรสำรองฐานข้อมูลก่อนดำเนินการ
        </Checkbox>
        <div style={{ marginTop: 20 }}>
          <Button
            danger
            type="primary"
            size="large"
            icon={<DeleteOutlined />}
            disabled={!canClear}
            loading={clearing}
            onClick={clearAll}
          >
            ล้างสมุดรายวันทั้งหมด
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AccountingDangerZonePage;
